import { computed } from 'vue'
import { useStorage, StorageSerializers } from '@vueuse/core'
import { useUserStore } from '@/stores/user.stores'
import { useStoreOperationsAnalyticsStore } from '@/stores/storeOperationsAnalytics.stores'
import { queryClient } from '@/plugins/vue-query'
import { clearClawValidDataCache } from '@/components/DashboardAccountInquiryPage/RevenueCard/composables/useClaw'

export const useContextSwitch = () => {
  const contextSwitchAccount = useStorage<{ userId: string; userName: string } | undefined>(
    'context-switch-user-id',
    undefined,
    sessionStorage,
    { serializer: StorageSerializers.object }
  )
  const { userInfo } = useUserStore()

  const updateContextSwitchAccount = (userId: string, userName: string) => {
    contextSwitchAccount.value = {
      userId: userId,
      userName: userName
    }
  }

  const clearContextSwitchAccount = () => {
    contextSwitchAccount.value = undefined
  }

  // 判斷是否已經切換過帳號，根據 contextSwitchAccount 是否有值來決定
  const isContextSwitched = computed(
    () => !!contextSwitchAccount.value && contextSwitchAccount.value.userId !== userInfo.userId
  )

  /**
   * 帳號切換（切進委任帳號／返回主帳號／委任被收回）共用的快取清除。
   * @remarks 切換當下，前一個帳號可能還有 API 請求在等待回應（例如
   *          store/operations-analytics 這類較慢的分析資料），`queryClient.clear()`
   *          只會清掉快取內容，不會中止還在飛行中的請求——沒先取消，舊帳號的回應晚到時
   *          仍會把資料寫回快取，蓋掉新帳號剛拿到的資料，所以要先 `cancelQueries()`
   *          讓這些請求真正中止，再清快取。
   *          validDataCache、storeOperationsAnalytics 這兩個 store 都活在 queryClient
   *          之外（前者是獨立的 Map，後者是持久化到 localStorage 的 Pinia store），
   *          `queryClient.clear()` 都清不到，需另外個別清除。
   */
  const clearAccountSwitchCache = async (): Promise<void> => {
    await queryClient.cancelQueries()
    queryClient.clear()
    clearClawValidDataCache()
    useStoreOperationsAnalyticsStore().reset()
  }

  return {
    contextSwitchAccount,
    updateContextSwitchAccount,
    clearContextSwitchAccount,
    isContextSwitched,
    clearAccountSwitchCache
  }
}
